import { useCallback, useMemo, useRef } from "react";
import pusher from "../utils/pusher";
import { hiddenChannelNameFor } from "../utils/conversationVisibility";
import { isConversationScopeRestricted } from "../utils/conversationScope";
import { getHumanTasksUserId, receivesHumanTasks } from "../views/dashboard/inicio/access";
import { HUMAN_TASK_EVENTS } from "../views/dashboard/inicio/constants/realtime-events";

/**
 * Eventos de Pusher a los que escucha el dashboard.
 *
 * Vive fuera del hook a propósito: como array dentro del cuerpo se recreaba en
 * cada render y arrastraba a las funciones que lo usan a cambiar de identidad.
 */
const SERVICES = [
  "onLinkedChannel",
  "newMessage",
  "newClient",
  "updateUserData",
  "deleteConversation",
  "archived",
  "archivedBroadcastConversation",
  "markAsRead",
  "markAsUnread",
  "conversationValuable",
  "messageStatus",
  "finishBotAssignment",
  "newUserName",
  "tagsUpdate",
  "unassignClient",
  "openClient",
  "groupAdmin",
  "conversationVisibilityChanged",
];

/**
 * Modo cola (límite de conversaciones por agente), spec §6. Sus payloads no
 * traen `type`, así que a estos el handler les llega además el nombre del
 * evento. Los de arriba se siguen entregando exactamente igual que antes.
 */
const NAMED_EVENTS = [
  "conversationSeated",
  "conversationUnseated",
  "conversationLimitCapacity",
  "conversationQueueChanged",
  "conversationLimitAgentLoad",
];

const ALL_EVENTS = SERVICES.concat(NAMED_EVENTS);

/**
 * Inicio (tareas para agentes humanos), spec §7: `taskOffered`, `taskUpdated`
 * y `taskRemoved` llegan por `agent_<userId>`, también sin `type`. Sólo se
 * atan para quien recibe tareas; para todos los demás los canales y eventos
 * son exactamente los de antes.
 */
const EVENTS_TO_UNBIND = ALL_EVENTS.concat(HUMAN_TASK_EVENTS);

const isUserAdmin = (rolName) =>
  rolName === "admin" || rolName === "superadmin" || rolName === "groupadmin";

/**
 * Admin features on `company_<companyId>` that are not conversations: the
 * queue-mode «Carga por agente» panel and queue counter, and the result of
 * linking WhatsApp. A conversation-restricted admin (Inicio, spec S12) gets
 * its conversations on `agent_<userId>` like an agent session, and keeps only
 * these on the company channel, so nobody else's conversation reaches its list.
 */
const COMPANY_PANEL_EVENTS = [
  "conversationQueueChanged",
  "conversationLimitAgentLoad",
  "onLinkedChannel",
];

/**
 * Suscribe el dashboard al canal de Pusher que le corresponde al usuario y
 * despacha cada evento entrante a `handleNotification`.
 *
 * ## Por qué este hook se reescribió (DASHBOARD-ZC-KK / -KV / -KT)
 *
 * La versión anterior devolvía `{ subscribe, unsubscribe }` como objeto literal
 * y recreaba `unsubscribe` en cada render. Ese objeto está en el array de
 * dependencias del efecto de `DashboardLayout` que llama a `subscribe()`, así
 * que **el efecto se rehacía en cada render del layout**: `pusher.unsubscribe()`
 * + `pusher.subscribe()` + re-bind de los 17 servicios, una y otra vez.
 *
 * Y no hacía falta ni navegar: `subscribe()` llamaba a `setChannel()`, lo que
 * provocaba un render, que cambiaba la identidad del binder, que rehacía el
 * efecto. Un solo montaje ya mandaba DOS suscripciones. A partir de ahí,
 * cualquier cambio de Redux que el layout observa (usuario, canales,
 * plantillas…) — o sea, constantemente en un chat con tráfico — repetía el
 * ciclo.
 *
 * Consecuencia para el asesor: durante cada ventana de churn el canal está
 * desatado y **los eventos que llegan en ese momento se pierden**. Es el
 * "no me llegan los mensajes en vivo, sólo al recargar" que reportaron
 * SCPodólogos, VINTE y SieteTrece. Y para Pusher: un goteo constante de
 * frames de suscripción/desuscripción por cada pestaña abierta.
 *
 * El arreglo es de identidad, no de lógica: el canal vive en un `ref` (no en
 * `useState`, que forzaba el render), `unsubscribe` se memoiza, y el objeto
 * devuelto es estable mientras no cambie el usuario. `subscribe()` además es
 * idempotente: llamarlo dos veces no abre una segunda suscripción.
 *
 * El `handleNotification` se lee desde un ref, así que un callback nuevo NO
 * obliga a resuscribirse — antes tampoco lo hacía (no estaba en las deps de
 * `useCallback`), pero por accidente: los handlers se quedaban con la versión
 * del primer bind. Ahora siempre se usa la última, sin tocar la suscripción.
 */
const useBinder = (user, handleNotification) => {
  // Canales REALMENTE suscritos. El primero es siempre el de siempre
  // (`company_` / `agent_`); el segundo, sólo para exentos con la función de
  // visibilidad activa, es `private-hidden-<userId>` (R6).
  // A conversation-restricted admin (S12) adds `company_` last, bound only
  // to COMPANY_PANEL_EVENTS.
  const channelsRef = useRef([]);
  const notifyRef = useRef(handleNotification);
  notifyRef.current = handleNotification;

  const companyId = user.data?.company?.id;
  const userId = user.data?.id;
  const rolName = user.data?.rol?.name;

  const isAdmin = isUserAdmin(rolName);
  // A conversation-restricted admin (S12) listens like an agent session.
  const isRestricted = isAdmin && isConversationScopeRestricted(user.data);
  const channelName = isAdmin && !isRestricted
    ? `company_${companyId}`
    : `agent_${userId}`;
  // ...and keeps the company channel only for COMPANY_PANEL_EVENTS.
  const companyPanelChannelName = isRestricted ? `company_${companyId}` : null;
  // Canal de las tareas de Inicio. Para un agente es el mismo de siempre; un
  // admin que recibe tareas lo suscribe además de `company_` (hoy sólo
  // escucha ese), y en él sólo se atan los eventos de tareas: los del chat ya
  // le llegan por `company_` y atarlos dos veces los duplicaría.
  // En una pestaña de sede las tareas llegan a la cuenta vinculada de esa sede
  // (docs/specs/2026-11-04-inicio-en-multisede), no a la del miembro.
  const taskUserId = getHumanTasksUserId(user.data);
  const taskChannelName = taskUserId && receivesHumanTasks(user.data) ? `agent_${taskUserId}` : null;
  // String, no objeto: así la identidad de `subscribe` sólo cambia cuando
  // cambia el canal privado que toca, no en cada render.
  const hiddenChannelName = hiddenChannelNameFor(user.data);

  const unsubscribe = useCallback(() => {
    const channels = channelsRef.current;
    if (channels.length === 0) return;

    channels.forEach((channel) => {
      console.log("Binder:: Unsubscribe to channel: ", channel.name);
      EVENTS_TO_UNBIND.forEach((service) => channel.unbind(service));
      // Se suelta el canal que REALMENTE se suscribió, no el que tocaría según
      // el usuario de ahora: si el usuario cambió, el nombre ya no coincide y
      // la versión anterior dejaba el canal viejo colgado.
      pusher.unsubscribe(channel.name);
    });
    channelsRef.current = [];
  }, []);

  const subscribe = useCallback(() => {
    if (channelsRef.current.length > 0) return channelsRef.current[0];
    if (!companyId) return null;

    const names = hiddenChannelName
      ? [channelName, hiddenChannelName]
      : [channelName];
    const taskOnlyChannel = taskChannelName && !names.includes(taskChannelName);
    if (taskOnlyChannel) names.push(taskChannelName);
    if (companyPanelChannelName) names.push(companyPanelChannelName);

    channelsRef.current = names.map((name) => {
      const channel = pusher.subscribe(name);
      console.log("Binder:: Subscribing to channel: ", channel.name);
      const isTaskOnly = taskOnlyChannel && name === taskChannelName;
      const isCompanyPanel = name === companyPanelChannelName;
      if (!isTaskOnly) {
        // Same delivery as always (named events also get their name); the
        // company panel channel binds only COMPANY_PANEL_EVENTS.
        const binds = (event) => !isCompanyPanel || COMPANY_PANEL_EVENTS.includes(event);
        SERVICES.filter(binds).forEach((service) => {
          channel.bind(service, (data) => notifyRef.current(data));
        });
        NAMED_EVENTS.filter(binds).forEach((event) => {
          channel.bind(event, (data) => notifyRef.current(data, event));
        });
      }
      if (name === taskChannelName) {
        HUMAN_TASK_EVENTS.forEach((event) => {
          channel.bind(event, (data) => notifyRef.current(data, event));
        });
      }
      return channel;
    });

    return channelsRef.current[0];
  }, [companyId, channelName, hiddenChannelName, taskChannelName, companyPanelChannelName]);

  return useMemo(() => ({ subscribe, unsubscribe }), [subscribe, unsubscribe]);
};

export default useBinder;
